/**
 * Status Update period helpers
 *
 * Shared between StatusUpdateComposeModal (period picker) and
 * StatusUpdateHistoryModal (history row labels) so a Monday-anchored
 * 7-day span always renders as "Week of ..." while any other span renders
 * as an explicit "From – To" range.
 *
 * IMPORTANT: a period bound (`weekStart` / `periodEnd`) is a *calendar
 * date*, not a moment in time. It must never be run through a timezone
 * conversion (`formatDateOnly`, `new Date(iso).toLocaleDateString()`, etc.)
 * — that can push an end-of-day UTC instant (e.g. `...T23:59:59.999Z`)
 * across midnight into the next day for timezones ahead of UTC. Instead,
 * every helper here operates on plain `YYYY-MM-DD` strings so the intended
 * calendar date is preserved exactly as stored.
 */

import { getISOWeek } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Extract the `YYYY-MM-DD` calendar date from an ISO datetime string (e.g. from the API). */
export function isoToYmd(iso: string): string {
  return iso.slice(0, 10);
}

function parseYmd(ymd: string): { y: number; m: number; d: number } {
  const [y, m, d] = ymd.split('-').map(Number);
  return { y, m: m || 1, d: d || 1 };
}

/**
 * Noon-local `Date` for a `YYYY-MM-DD` string — used only for day-of-week /
 * day-diff / ISO-week arithmetic (never for display). Noon avoids DST
 * edge cases that midnight-local can hit.
 */
function noonLocal(ymd: string): Date {
  const { y, m, d } = parseYmd(ymd);
  return new Date(y, m - 1, d, 12);
}

/** `dd.MM.yyyy` from a `YYYY-MM-DD` string via pure string manipulation — no `Date`, no timezone. */
function formatDmy(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y}`;
}

/** True when `startYmd` is a Monday and `endYmd` is exactly 6 calendar days later (inclusive Mon–Sun week). */
export function isWeekSpan(startYmd: string, endYmd: string): boolean {
  const start = noonLocal(startYmd);
  if (start.getDay() !== 1) return false;
  const end = noonLocal(endYmd);
  const diffDays = Math.round((end.getTime() - start.getTime()) / ONE_DAY_MS);
  return diffDays === 6;
}

/**
 * Renders a period as "Week of {date} · KW {n}" (Monday-anchored 7-day span,
 * ISO week number appended) or "{start} – {end}" (custom range, no week
 * number). Operates purely on `YYYY-MM-DD` strings — never converts through
 * a timezone, so an end-of-day stored bound never appears to roll over.
 *
 * `weekNumberLabel` lets callers localize the "KW {n}" token (the primary
 * audience is German — "KW" = Kalenderwoche) via i18n, e.g.:
 *   (w) => t('status_compose_week_number', { week: w, defaultValue: `KW ${w}` })
 * When omitted, falls back to the same `KW {n}` string inline so callers
 * that haven't wired i18n yet still get a correct label.
 */
export function formatPeriodLabel(
  startYmd: string,
  endYmd: string,
  weekOfLabel = 'Week of',
  weekNumberLabel?: (week: number) => string,
): string {
  if (isWeekSpan(startYmd, endYmd)) {
    const week = getISOWeek(noonLocal(startYmd));
    const weekLabel = weekNumberLabel ? weekNumberLabel(week) : `KW ${week}`;
    return `${weekOfLabel} ${formatDmy(startYmd)} · ${weekLabel}`;
  }
  return `${formatDmy(startYmd)} – ${formatDmy(endYmd)}`;
}

/** Whole calendar days spanned by [startYmd, endYmd], inclusive. */
export function daysInPeriod(startYmd: string, endYmd: string): number {
  const start = noonLocal(startYmd);
  const end = noonLocal(endYmd);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / ONE_DAY_MS) + 1);
}

/** Local YYYY-MM-DD for binding to a native `<input type="date">`. */
export function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse a native `<input type="date">` value ("YYYY-MM-DD") as a LOCAL midnight Date. */
export function fromDateInputValue(value: string): Date {
  const [y, m, day] = value.split('-').map(Number);
  const d = new Date(y, (m || 1) - 1, day || 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

// ── Timeline span helpers (timezone-aware) ─────────────────────────────────────
//
// Used by TimelineControls (Brand Portfolio Timeline). All boundary math here
// is computed against the ACCOUNT timezone cascade (`useTimezone()` →
// user → team → Europe/Berlin default), not the browser engine's local
// timezone. This matters because `useTimelineWindow`'s per-day column ISO
// keys are already formatted in that account timezone (via
// `Intl.DateTimeFormat(..., { timeZone })`) — if the `from`/`to` instants
// sent to the API were computed in browser-local time instead, a user whose
// browser clock differs from their account timezone would get boundary-day
// drift: posts on the first/last calendar day of the range silently clipped,
// and (in week-aggregation mode) column grouping shifted by a day.
//
// These mirror the intent of the private `getWeekStart`/`getWeekEnd` pair in
// StatusUpdateComposeModal (Monday-anchored week), exported here so other
// components can share the same Monday-week convention without importing
// from a modal. StatusUpdateComposeModal's pair is browser-local by design
// (it drives a period *label*, not a server fetch window) and is
// intentionally NOT touched here.

/** Y/M/D calendar parts (1-indexed month) that `date` resolves to when
 *  viewed in `timeZone` — the tz-aware equivalent of `getFullYear`/
 *  `getMonth`/`getDate`. */
function getZonedYmd(date: Date, timeZone: string): { y: number; m: number; d: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return { y: get('year'), m: get('month'), d: get('day') };
}

/** Adds `days` (may be negative) to a Y/M/D calendar date. Pure calendar-date
 *  arithmetic via `Date.UTC` — never touches any timezone, so it can't be
 *  thrown off by DST. */
function addDaysYmd(
  ymd: { y: number; m: number; d: number },
  days: number,
): { y: number; m: number; d: number } {
  const dt = new Date(Date.UTC(ymd.y, ymd.m - 1, ymd.d + days));
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

/** Day-of-week (`0` = Sunday … `6` = Saturday) of a Y/M/D calendar date.
 *  Depends only on the calendar date, never on a timezone conversion. */
function ymdWeekday(ymd: { y: number; m: number; d: number }): number {
  return new Date(Date.UTC(ymd.y, ymd.m - 1, ymd.d)).getUTCDay();
}

/** The correct UTC instant for a wall-clock moment (Y, 1-indexed M, D, h, m,
 *  s, ms) interpreted in `timeZone` — e.g. "2026-08-01 00:00:00 in
 *  Europe/Berlin" → the UTC `Date` for that exact moment, DST-aware. */
function zonedWallClockToUtc(
  y: number,
  m: number,
  d: number,
  h: number,
  mi: number,
  s: number,
  ms: number,
  timeZone: string,
): Date {
  // `fromZonedTime` reads the wall-clock fields (year/month/.../ms) off the
  // `Date` object it's given via the browser-local getters and treats them
  // as the local time in `timeZone`. Building the `Date` via the local
  // constructor with the exact same fields round-trips those getters
  // losslessly regardless of the browser engine's own timezone, so this is
  // safe on any machine.
  return fromZonedTime(new Date(y, m - 1, d, h, mi, s, ms), timeZone);
}

/** Last calendar day (1-indexed month) of month `m` in year `y`. */
function lastDayOfMonth(y: number, m: number): { y: number; m: number; d: number } {
  // Day 0 of the following month == the last day of month `m`.
  const nextM = m === 12 ? 1 : m + 1;
  const nextY = m === 12 ? y + 1 : y;
  const dt = new Date(nextY, nextM - 1, 0);
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
}

/** Monday 00:00:00.000 (in `timeZone`) of the week containing `date`. */
export function getWeekStart(date: Date, timeZone: string): Date {
  const ymd = getZonedYmd(date, timeZone);
  const day = ymdWeekday(ymd); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day; // Monday = 0 offset
  const startYmd = addDaysYmd(ymd, diff);
  return zonedWallClockToUtc(startYmd.y, startYmd.m, startYmd.d, 0, 0, 0, 0, timeZone);
}

/** Sunday 23:59:59.999 (in `timeZone`) of the week that starts at `start`
 *  (a Monday returned by `getWeekStart` in the SAME `timeZone`). */
export function getWeekEnd(start: Date, timeZone: string): Date {
  const ymd = getZonedYmd(start, timeZone);
  const endYmd = addDaysYmd(ymd, 6);
  return zonedWallClockToUtc(endYmd.y, endYmd.m, endYmd.d, 23, 59, 59, 999, timeZone);
}

/** Span presets supported by the Brand Portfolio Timeline navigation. */
export type TimelinePreset = '2w' | 'month' | 'quarter' | 'custom';

/**
 * Resolves a preset into a concrete `{from, to}` window anchored at
 * `anchor`, with every boundary computed against the calendar in
 * `timeZone` (NOT the browser engine's local timezone).
 *
 * - `'2w'` is a today-anchored ROLLING window: 7 days back to 7 days forward
 *   (day-granular, not calendar-week aligned).
 * - `'month'` / `'quarter'` snap to the calendar month/quarter containing
 *   `anchor`, as seen in `timeZone`.
 * - `'custom'` has no natural span of its own — callers drive `from`/`to`
 *   directly via the date-range picker; this branch just returns a harmless
 *   single-day default so the function is total.
 */
export function getSpanRange(
  preset: TimelinePreset,
  anchor: Date,
  timeZone: string,
): { from: Date; to: Date } {
  switch (preset) {
    case '2w': {
      const ymd = getZonedYmd(anchor, timeZone);
      const fromYmd = addDaysYmd(ymd, -7);
      const toYmd = addDaysYmd(ymd, 7);
      return {
        from: zonedWallClockToUtc(fromYmd.y, fromYmd.m, fromYmd.d, 0, 0, 0, 0, timeZone),
        to: zonedWallClockToUtc(toYmd.y, toYmd.m, toYmd.d, 23, 59, 59, 999, timeZone),
      };
    }
    case 'month': {
      const { y, m } = getZonedYmd(anchor, timeZone);
      const last = lastDayOfMonth(y, m);
      return {
        from: zonedWallClockToUtc(y, m, 1, 0, 0, 0, 0, timeZone),
        to: zonedWallClockToUtc(last.y, last.m, last.d, 23, 59, 59, 999, timeZone),
      };
    }
    case 'quarter': {
      const { y, m } = getZonedYmd(anchor, timeZone);
      const qStartMonth = Math.floor((m - 1) / 3) * 3 + 1; // 1, 4, 7, or 10
      const qEndMonth = qStartMonth + 2;
      const last = lastDayOfMonth(y, qEndMonth);
      return {
        from: zonedWallClockToUtc(y, qStartMonth, 1, 0, 0, 0, 0, timeZone),
        to: zonedWallClockToUtc(last.y, last.m, last.d, 23, 59, 59, 999, timeZone),
      };
    }
    case 'custom':
    default:
      return { from: anchor, to: anchor };
  }
}

/**
 * Shifts `current` by one "step" of `preset`'s natural unit, in `direction`
 * (`-1` = back, `1` = forward), with the shifted boundaries re-snapped
 * against the calendar in `timeZone`. Pure — never reads `Date.now()`.
 *
 * - `'2w'` steps by 14 days.
 * - `'month'` steps to the previous/next calendar month (re-snapped to that
 *   month's start/end, so month-length changes don't leave a stale range).
 * - `'quarter'` steps to the previous/next calendar quarter (same
 *   re-snapping).
 * - `'custom'` shifts the whole window by its own current length, preserving
 *   the span the user picked.
 */
export function stepRange(
  preset: TimelinePreset,
  current: { from: Date; to: Date },
  direction: -1 | 1,
  timeZone: string,
): { from: Date; to: Date } {
  switch (preset) {
    case '2w': {
      const fromYmd = addDaysYmd(getZonedYmd(current.from, timeZone), direction * 14);
      const toYmd = addDaysYmd(getZonedYmd(current.to, timeZone), direction * 14);
      return {
        from: zonedWallClockToUtc(fromYmd.y, fromYmd.m, fromYmd.d, 0, 0, 0, 0, timeZone),
        to: zonedWallClockToUtc(toYmd.y, toYmd.m, toYmd.d, 23, 59, 59, 999, timeZone),
      };
    }
    case 'month': {
      const { y, m } = getZonedYmd(current.from, timeZone);
      let nm = m + direction;
      let ny = y;
      if (nm < 1) {
        nm = 12;
        ny -= 1;
      } else if (nm > 12) {
        nm = 1;
        ny += 1;
      }
      // Noon avoids any DST-transition edge case landing the anchor on the
      // wrong calendar day before `getSpanRange` re-derives Y/M from it.
      const anchor = zonedWallClockToUtc(ny, nm, 1, 12, 0, 0, 0, timeZone);
      return getSpanRange('month', anchor, timeZone);
    }
    case 'quarter': {
      const { y, m } = getZonedYmd(current.from, timeZone);
      const qStartMonth = Math.floor((m - 1) / 3) * 3 + 1;
      let nq = qStartMonth + direction * 3;
      let ny = y;
      if (nq < 1) {
        nq += 12;
        ny -= 1;
      } else if (nq > 12) {
        nq -= 12;
        ny += 1;
      }
      const anchor = zonedWallClockToUtc(ny, nq, 1, 12, 0, 0, 0, timeZone);
      return getSpanRange('quarter', anchor, timeZone);
    }
    case 'custom':
    default: {
      const spanMs = current.to.getTime() - current.from.getTime();
      const shiftMs = direction * (spanMs + 1); // +1 to move past the inclusive end
      return {
        from: new Date(current.from.getTime() + shiftMs),
        to: new Date(current.to.getTime() + shiftMs),
      };
    }
  }
}
